import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getPriceLists, uploadPriceListWithProgress, deletePriceList } from '../api/client'
import type { PriceList } from '../types'

type UploadPhase = 'idle' | 'uploading' | 'processing'

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function PriceLists() {
  const { t } = useTranslation()
  const [lists, setLists] = useState<PriceList[]>([])
  const [loading, setLoading] = useState(true)
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>('idle')
  const [uploadPct, setUploadPct] = useState(0)
  const [uploadFileName, setUploadFileName] = useState<string | null>(null)
  const [uploadFileSize, setUploadFileSize] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => { load() }, [])

  async function load() {
    try {
      setLoading(true)
      setLists(await getPriceLists())
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('price_lists.error_load'))
    } finally {
      setLoading(false)
    }
  }

  const MAX_FILE_BYTES = 200 * 1024 * 1024 // 200 MB — overeenkomend met backend limiet

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setError(null)

    if (file.size > MAX_FILE_BYTES) {
      setError(t('price_lists.error_file_too_large', { size: formatBytes(file.size), max: formatBytes(MAX_FILE_BYTES) }))
      return
    }

    setUploadFileName(file.name)
    setUploadFileSize(file.size)
    setUploadPct(0)
    setUploadPhase('uploading')
    try {
      const created = await uploadPriceListWithProgress(file, (pct) => {
        setUploadPct(pct)
        if (pct === 100) setUploadPhase('processing')
      })
      setLists((prev) => [created, ...prev])
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('price_lists.error_upload'))
    } finally {
      setUploadPhase('idle')
      setUploadFileName(null)
      setUploadFileSize(null)
      setUploadPct(0)
    }
  }

  async function handleDelete(id: number) {
    setError(null)
    try {
      await deletePriceList(id)
      setLists((prev) => prev.filter((pl) => pl.id !== id))
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('price_lists.error_delete'))
    } finally {
      setDeleteConfirm(null)
    }
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="space-y-6">
      {/* Upload */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          uploadPhase !== 'idle'
            ? 'border-blue-300 bg-blue-50 dark:bg-blue-900/20 cursor-default'
            : 'border-gray-200 dark:border-gray-700 hover:border-blue-400 cursor-pointer'
        }`}
        onClick={() => uploadPhase === 'idle' && fileRef.current?.click()}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".xml,.xlsx,.xls,.xlsm"
          className="hidden"
          onChange={handleFileChange}
        />

        {uploadPhase === 'idle' && (
          <>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('price_lists.upload_click_prompt')}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {t('price_lists.upload_hint')}
            </p>
          </>
        )}

        {uploadPhase !== 'idle' && (
          <div className="space-y-3">
            <div className="flex items-center justify-center gap-2 text-sm text-blue-800">
              <span className="font-medium truncate max-w-xs">{uploadFileName}</span>
              {uploadFileSize !== null && (
                <span className="text-blue-500 text-xs shrink-0">({formatBytes(uploadFileSize)})</span>
              )}
            </div>

            {uploadPhase === 'uploading' && (
              <>
                <div className="w-full bg-blue-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-200"
                    style={{ width: `${uploadPct}%` }}
                  />
                </div>
                <p className="text-xs text-blue-600 font-medium">
                  {t('price_lists.uploading_progress', { pct: uploadPct })}
                </p>
              </>
            )}

            {uploadPhase === 'processing' && (
              <>
                <div className="w-full bg-blue-100 rounded-full h-2 overflow-hidden">
                  <div className="bg-blue-500 h-2 rounded-full animate-pulse w-full" />
                </div>
                <p className="text-xs text-blue-600 font-medium">{t('price_lists.processing_status')}</p>
              </>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-700 dark:text-red-300 rounded-xl px-4 py-3 text-sm flex items-start justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-4 text-red-400 hover:text-red-600">
            &times;
          </button>
        </div>
      )}

      {/* Lijst */}
      {loading ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">{t('price_lists.loading')}</p>
      ) : lists.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">{t('price_lists.empty_state')}</p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">{t('price_lists.col_name')}</th>
                <th className="px-4 py-3 text-left">{t('price_lists.col_supplier_gln')}</th>
                <th className="px-4 py-3 text-left">{t('price_lists.col_buyer_gln')}</th>
                <th className="px-4 py-3 text-right">{t('price_lists.col_rows')}</th>
                <th className="px-4 py-3 text-left">{t('price_lists.col_format')}</th>
                <th className="px-4 py-3 text-left">{t('price_lists.col_uploaded')}</th>
                <th className="px-4 py-3 text-left">{t('price_lists.col_uploaded_by')}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {lists.map((pl) => (
                <tr key={pl.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-200">{pl.name}</td>
                  <td className="px-4 py-3 font-mono text-gray-600 dark:text-gray-400">{pl.supplier_gln}</td>
                  <td className="px-4 py-3 font-mono text-gray-600 dark:text-gray-400">{pl.buyer_gln}</td>
                  <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{pl.row_count.toLocaleString('nl-NL')}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      pl.source_format === 'excel'
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                        : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    }`}>
                      {pl.source_format === 'excel' ? t('price_lists.format_excel') : t('price_lists.format_xml')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{formatDate(pl.created_at)}</td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{pl.created_by}</td>
                  <td className="px-4 py-3 text-right">
                    {deleteConfirm === pl.id ? (
                      <span className="flex items-center justify-end gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">{t('price_lists.delete_confirm_prompt')}</span>
                        <button
                          onClick={() => handleDelete(pl.id)}
                          className="text-xs text-red-600 hover:underline font-medium"
                        >
                          {t('price_lists.delete_confirm_yes')}
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="text-xs text-gray-400 hover:underline"
                        >
                          {t('price_lists.delete_confirm_no')}
                        </button>
                      </span>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(pl.id)}
                        className="text-xs text-red-400 hover:text-red-600"
                      >
                        {t('price_lists.delete_button')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
